import './UserPreviewCardProfil.scss';

import { InfoRounded, Settings } from '@mui/icons-material';
import classNames from 'classnames';
import { GameProfil } from 'domain/gameProfil/GameProfil';
import {
  getName,
  getNeonBarreStyle,
  getNeonBorderStyle,
} from 'domain/profil/Profil.func';
import { UserProfil } from 'domain/profil/UserProfil';
import {
  selectConnectedUserBlocked,
  selectConnectedUserFriend,
} from 'domain/relation/store/RelationSlice';
import { GameSelectionCaroussel } from 'primary/Components/GameSelectionCaroussel/GameSelectionCaroussel';
import { IconFlag } from 'primary/Components/Icon/IconFlag';
import { Neon } from 'primary/Components/Neon/Neon';
import { NoGamesIllustration } from 'primary/Components/NoGames/NoGamesIllustration';
import { PlatformsRow } from 'primary/Components/PlatformsRow/PlatformsRow';
import {
  PopperButton,
  PopperButtonsConf,
} from 'primary/Components/PopperButton/PopperButton';
import { ActionButtonAddFriend } from 'primary/Components/Profils/User/ActionButton/ActionButtonAddFriend';
import { ActionButtonBlock } from 'primary/Components/Profils/User/ActionButton/ActionButtonBlock';
import { ActionButtonInviteEvent } from 'primary/Components/Profils/User/ActionButton/ActionButtonInviteEvent';
import { ActionButtonInviteStructure } from 'primary/Components/Profils/User/ActionButton/ActionButtonInviteStructure';
import { ActionButtonRemoveFriend } from 'primary/Components/Profils/User/ActionButton/ActionButtonRemoveFriend';
import { ActionButtonSendMessage } from 'primary/Components/Profils/User/ActionButton/ActionButtonSendMessage';
import { ActionButtonUnBlock } from 'primary/Components/Profils/User/ActionButton/ActionButtonUnBlock';
import { TeamCreatingAction } from 'primary/Components/Profils/User/TeamCreatingActions';
import { WrapperOtherProfilPhotoOverview } from 'primary/Components/User/WrapperOtherProfilPhotoOverview';
import { routesConfig } from 'primary/Configs/Routes.config';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate } from 'primary/hooks/useTranslate';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from 'store';

interface UserPreviewCardProfilProps {
  profil: UserProfil;
  userName: string;
  className?: string;
  inPage?: boolean;
  draftPhoto?: File;
  noAction?: boolean;
}

interface ToggleGameStatsProps {
  profil: UserProfil;
  inPage?: boolean;
}

const ToggleGameStats = ({ profil, inPage }: ToggleGameStatsProps) => {
  return (
    <div className={'unpyStatsWithGames'}>
      <div className={'-games'}>
        <GameSelectionCaroussel
          gamesToShow={profil.games.map((value: GameProfil) => value.game.code)}
          selectedValues={[]}
          rows={1}
          slidePerRow={1}
          slidesToScroll={1}
          embeded
          infinite
          withoutDots
          center
        />
      </div>
    </div>
  );
};

export const UserPreviewCardProfil = ({
  profil,
  className,
  inPage,
  draftPhoto,
  noAction,
}: UserPreviewCardProfilProps) => {
  const neonBorderStyle = getNeonBorderStyle(profil?.neonBorder);
  const neonPhotoStyle = getNeonBorderStyle(profil?.neonPhoto);
  const neonLitleBarStyle = getNeonBarreStyle(profil?.neonLitleBar);
  const neonBarStyle = getNeonBarreStyle(profil?.neonBar);
  const { authRepository } = useContextDependency();
  const friends = useSelector(selectConnectedUserFriend);
  const block = useSelector(selectConnectedUserBlocked);

  const navigation = useNavigate();
  const handleClick = useCallback(
    (e) => {
      navigation(routesConfig.mercatoUserProfil.to(profil.id));
      return e;
    },
    [profil],
  );
  const teamCreating = useSelector((state: RootState) => state.teamCreating.teamCreating);

  const handleClickParam = useCallback(() => {
    navigation(routesConfig.parametersUser.to());
  }, []);
  const alreadyFriend = friends?.find(
    (f) => f.invited.idProfil === profil.id || f.owner.idProfil === profil.id,
  );
  const alreadyBlocked = block?.find((b) => b.invited.idProfil === profil.id);

  const t = useTranslate();
  // @ts-ignore
  const conf: PopperButtonsConf[] = useMemo(() => {
    if (profil.id === authRepository?.currentUser?.userProfil.id) {
      return [
        {
          label: t('profil.userProfilAction.infosMore'),
          action: handleClick,
          icon: <InfoRounded />,
        },
        {
          label: t('profil.userProfilAction.parameters'),
          action: handleClickParam,
          icon: <Settings />,
        },
      ];
    }
    return [
      {
        label: t('profil.userProfilAction.infosMore'),
        action: handleClick,
        icon: <InfoRounded />,
      },
      {
        button: alreadyFriend ? (
          <ActionButtonRemoveFriend profil={profil} withIcon />
        ) : (
          <ActionButtonAddFriend profil={profil} withIcon />
        ),
      },
      {
        button: <ActionButtonSendMessage profil={profil} withIcon />,
      },
      {
        button: <ActionButtonInviteStructure profil={profil} withIcon />,
      },
      {
        button: <ActionButtonInviteEvent profil={profil} withIcon />,
      },
      {
        button: alreadyBlocked ? (
          <ActionButtonUnBlock profil={profil} withIcon />
        ) : (
          <ActionButtonBlock profil={profil} withIcon />
        ),
      },
    ];
  }, [profil, friends, block]);

  return (
    <div className={'-paddingWrapper'}>
      <div className={'rightBarMenu'}>
        {!noAction && !teamCreating && (
          <PopperButton
            conf={conf}
            popperName={'user-profil-preview'}
            placement={'auto'}
          />
        )}
        <TeamCreatingAction profil={profil} />
      </div>

      <div
        key={profil.id}
        className={classNames(
          'userPreviewCardProfil',
          className,
          { '-hoverable': !noAction },
          { '-pointerable': !noAction },
        )}
        style={{
          backgroundImage: `url("${profil.backgroundImage?.publicUrlSm}")`,
          backgroundSize: 'cover',
          ...(profil?.neonBorder ? neonBorderStyle : {}),
        }}
        onTouchStart={undefined}
        onTouchEnd={undefined}
      >
        {noAction && (
          <div className={classNames('background__transp')}>
            <div className={'userPreviewCardProfil__header'}>
              <div
                className={classNames('userPreviewCardProfil__header-photo', {
                  '-inPage': !!inPage,
                })}
              >
                <WrapperOtherProfilPhotoOverview
                  displayName={getName(profil)}
                  photo={
                    draftPhoto ? URL.createObjectURL(draftPhoto) : profil.publicPhotoUrl
                  }
                  style={neonPhotoStyle}
                  idUser={profil.userId as number}
                  size={inPage ? 'small' : 'big'}
                />
                {profil.country && (
                  <span className={'-lang'} title={profil.country.libelle}>
                    <IconFlag codeLang={undefined} codeISO={profil.country.code} />
                  </span>
                )}
              </div>
              <div className={classNames('userPreviewCardProfil__header-infos')}>
                <div className={'-username'} title={profil.idPlayer}>
                  <h3>{profil.idPlayer}</h3>
                </div>
                <Neon
                  className={'neon -marged -threeQuarter'}
                  style={neonLitleBarStyle}
                />
                <div className={'-badges'}>
                  <PlatformsRow
                    variant={'contained'}
                    platforms={profil.platforms?.map((platform) => platform.code)}
                  />
                </div>
              </div>
            </div>
            <div className={'userPreviewCardProfil__content'}>
              <Neon
                width={'75%'}
                className={'neon -threeQuarter -marginVertical'}
                style={neonBarStyle}
              />
              <div className={'userPreviewCardProfil__content-row -third'}>
                {profil?.games?.length > 0 && (
                  <ToggleGameStats profil={profil} inPage={inPage} />
                )}
                {profil?.games?.length === 0 && <NoGamesIllustration />}
              </div>
            </div>
          </div>
        )}
        {!noAction && (
          <Link
            to={routesConfig.mercatoUserProfil.to(profil.id)}
            className={'-linkWrapper'}
          >
            <div className={classNames('background__transp')}>
              <div className={'userPreviewCardProfil__header'}>
                <div
                  className={classNames('userPreviewCardProfil__header-photo', {
                    '-inPage': !!inPage,
                  })}
                >
                  <WrapperOtherProfilPhotoOverview
                    displayName={getName(profil)}
                    photo={
                      draftPhoto ? URL.createObjectURL(draftPhoto) : profil.publicPhotoUrl
                    }
                    className={classNames('photo')}
                    style={neonPhotoStyle}
                    idUser={profil.userId as number}
                    size={inPage ? 'small' : 'big'}
                  />
                  {profil.country && (
                    <span className={'-lang'} title={profil.country.libelle}>
                      <IconFlag codeLang={undefined} codeISO={profil.country.code} />
                    </span>
                  )}
                </div>
                <div className={classNames('userPreviewCardProfil__header-infos')}>
                  <div className={'-username'} title={profil.idPlayer}>
                    <h3>{profil.idPlayer}</h3>
                  </div>
                  <Neon
                    className={'neon -marged -threeQuarter'}
                    style={neonLitleBarStyle}
                  />
                  <div className={'-badges'}>
                    <PlatformsRow
                      variant={'contained'}
                      platforms={profil.platforms?.map((platform) => platform.code)}
                    />
                  </div>
                </div>
              </div>
              <div className={'userPreviewCardProfil__content'}>
                <Neon
                  width={'75%'}
                  className={'neon -threeQuarter -marginVertical'}
                  style={neonBarStyle}
                />
                <div className={'userPreviewCardProfil__content-row -third'}>
                  {profil?.games?.length > 0 && (
                    <ToggleGameStats profil={profil} inPage={inPage} />
                  )}
                  {profil?.games?.length === 0 && <NoGamesIllustration />}
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};
